<template>
  <div class="category-container">
    <h2 class="page-title">游戏分类管理</h2>
    
    <!-- 添加分类按钮 -->
    <div class="add-category-section">
      <el-input
        v-model="newCategoryName"
        placeholder="请输入新分类名称"
        style="width: 300px; margin-right: 10px;"
        @keyup.enter="addCategory"
      >
        <template #append>
          <el-button icon="el-icon-plus" @click="addCategory">添加</el-button>
        </template>
      </el-input>
      <el-button type="success" icon="el-icon-upload" @click="openBulkDialog">批量上传</el-button>
    </div>
    
    <!-- 分类列表表格 -->
    <div class="category-table-section">
      <div style="margin-bottom: 10px;">
        <el-button 
          type="primary" 
          size="small" 
          :loading="savingOrder"
          @click="saveCategoryOrder"
          :disabled="!orderChanged"
        >
          {{ savingOrder ? '保存中...' : '保存排序' }}
        </el-button>
        <el-button 
          size="small" 
          @click="resetCategoryOrder"
          :disabled="!orderChanged"
        >
          重置
        </el-button>
        <span style="margin-left: 10px; color: #909399; font-size: 12px;">
          提示：拖拽左侧图标可调整分类顺序
        </span>
      </div>
      <el-table 
        v-loading="loading" 
        :data="categories" 
        style="width: 100%"
        row-key="name"
      >
        <el-table-column width="50" label="拖拽">
          <template #default>
            <el-icon class="drag-handle" style="cursor: move; color: #909399; font-size: 16px;"><Rank /></el-icon>
          </template>
        </el-table-column>
        <el-table-column label="序号" width="80" :index="(index) => index + 1"></el-table-column>
        <el-table-column label="分类名称" prop="name" min-width="200">
          <template #default="scope">
            <span v-if="!scope.row.editing">{{ scope.row.name || scope.row }}</span>
            <el-input
              v-else
              v-model="scope.row.editName"
              @keyup.enter="saveEditInline(scope.row)"
              @blur="saveEditInline(scope.row)"
              size="small"
              style="width: 200px"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right">
          <template #default="scope">
            <div style="display: flex; align-items: center; gap: 5px;">
              <el-button
                v-if="!scope.row.editing"
                type="primary"
                size="small"
                icon="el-icon-edit"
                @click="startEditInline(scope.row)"
              >编辑</el-button>
              <el-button
                v-else
                type="success"
                size="small"
                icon="el-icon-check"
                @click="saveEditInline(scope.row)"
              >保存</el-button>
              <el-button
                v-if="scope.row.editing"
                size="small"
                @click="cancelEditInline(scope.row)"
              >取消</el-button>
              <el-button
                type="primary"
                size="small"
                @click="openArticleDialog(scope.row.name || scope.row)"
              >文章</el-button>
              <el-button
                type="danger"
                size="small"
                icon="el-icon-delete"
                @click="deleteCategory(scope.row.name || scope.row)"
              >删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>
    
    <!-- 编辑分类对话框 -->
    <el-dialog
      :title="'编辑分类: ' + editingCategory"
      :visible.sync="editDialogVisible"
      width="30%"
    >
      <el-input
        v-model="editedCategoryName"
        placeholder="请输入新的分类名称"
        @keyup.enter="saveEdit"
      ></el-input>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="editDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="saveEdit">保存</el-button>
        </span>
      </template>
    </el-dialog>
    
    <!-- 批量上传对话框 -->
    <el-dialog
      v-model="bulkDialogVisible"
      title="批量上传分类"
      width="80%"
      :before-close="resetBulkDialog"
    >
      <div style="margin-bottom: 20px">
        <el-upload
          :auto-upload="false"
          :on-change="handleFileChange"
          :show-file-list="false"
          accept=".json"
          drag
        >
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">
            将 JSON 文件拖到此处，或<em>点击上传</em>
          </div>
          <template #tip>
            <div class="el-upload__tip">
              支持 JSON 格式文件，格式参考 categories_report.json
            </div>
          </template>
        </el-upload>
      </div>
      
      <el-input
        v-model="bulkJsonText"
        type="textarea"
        :rows="15"
        placeholder='或者直接粘贴 JSON 数据（格式: {"list": [...], "stats": {...}} 或 ["Category1", "Category2", ...]）'
      />
      
      <template #footer>
        <el-button @click="resetBulkDialog">取消</el-button>
        <el-button type="primary" :loading="bulkLoading" @click="submitBulkUpload">上传</el-button>
      </template>
    </el-dialog>
    
    <!-- 批量上传结果对话框 -->
    <el-dialog v-model="bulkResultDialogVisible" title="批量上传结果" width="70%">
      <div>
        <el-alert
          :title="`总计: ${bulkResult.total} | 成功: ${bulkResult.successCount} | 失败: ${bulkResult.failedCount}`"
          :type="bulkResult.failedCount > 0 ? 'warning' : 'success'"
          :closable="false"
          style="margin-bottom: 20px"
        />
        
        <el-tabs v-model="activeResultTab">
          <el-tab-pane label="成功列表" name="success">
            <el-table :data="bulkResult.success" max-height="400">
              <el-table-column label="序号" prop="index" width="80"></el-table-column>
              <el-table-column label="分类名称" prop="name"></el-table-column>
            </el-table>
          </el-tab-pane>
          <el-tab-pane label="失败列表" name="failed">
            <el-table :data="bulkResult.failed" max-height="400">
              <el-table-column label="序号" prop="index" width="80"></el-table-column>
              <el-table-column label="分类名称" prop="name"></el-table-column>
              <el-table-column label="错误信息" prop="error" show-overflow-tooltip></el-table-column>
            </el-table>
          </el-tab-pane>
        </el-tabs>
      </div>
      <template #footer>
        <el-button type="primary" @click="bulkResultDialogVisible = false; fetchCategories()">确定</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="articleDialogVisible" title="分类文章编辑" width="70%" :close-on-click-modal="false">
      <el-form label-width="90px">
        <el-form-item label="分类名称">
          <el-input :model-value="articleForm.categoryName" disabled />
        </el-form-item>
        <el-form-item label="文章标题">
          <el-input v-model="articleForm.title" placeholder="请输入文章标题" />
        </el-form-item>
        <el-tabs v-model="articleTab">
          <el-tab-pane name="edit" label="编辑">
            <el-input v-model="articleForm.content" type="textarea" :rows="18" placeholder="请输入 Markdown 内容" />
          </el-tab-pane>
          <el-tab-pane name="preview" label="预览">
            <div class="markdown-preview" v-html="renderMarkdown(articleForm.content)"></div>
          </el-tab-pane>
        </el-tabs>
      </el-form>
      <template #footer>
        <el-button @click="articleDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="articleSaving" @click="saveCategoryArticle">保存文章</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {CategoryApi} from '../../api/categoryApi';
import {CategoryArticleApi} from '../../api/categoryArticleApi';
import {ElMessage, ElMessageBox} from 'element-plus';
import {UploadFilled, Rank} from '@element-plus/icons-vue';
// 动态导入 sortablejs
let Sortable = null;
// 使用动态 import 加载 sortablejs（如果已安装）
const loadSortable = async () => {
  try {
    const sortableModule = await import('sortablejs');
    Sortable = sortableModule.default || sortableModule;
  } catch (e) {
    console.warn('sortablejs 未安装，拖拽排序功能将不可用。请运行: npm install sortablejs');
  }
};

export default {
  name: 'Category',
  components: {
    UploadFilled,
    Rank
  },
  data() {
    return {
      categories: [],
      originalCategories: [], // 保存原始顺序，用于重置
      loading: false,
      newCategoryName: '',
      editDialogVisible: false,
      editingCategory: '',
      editedCategoryName: '',
      // 批量上传相关
      bulkDialogVisible: false,
      bulkJsonText: '',
      bulkLoading: false,
      bulkResultDialogVisible: false,
      bulkResult: {
        total: 0,
        successCount: 0,
        failedCount: 0,
        success: [],
        failed: []
      },
      activeResultTab: 'success',
      // 排序相关
      sortableInstance: null,
      orderChanged: false,
      savingOrder: false,
      // 分类文章
      articleDialogVisible: false,
      articleSaving: false,
      articleTab: 'edit',
      articleForm: {
        id: '',
        categoryName: '',
        title: '',
        content: ''
      }
    };
  },
  methods: {
    renderMarkdown(text) {
      const escaped = (text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return escaped
        .replace(/^### (.*)$/gm, '<h3>$1</h3>')
        .replace(/^## (.*)$/gm, '<h2>$1</h2>')
        .replace(/^# (.*)$/gm, '<h1>$1</h1>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br/>');
    },
    async openArticleDialog(categoryName) {
      const name = (categoryName || '').trim();
      if (!name) return;
      this.articleDialogVisible = true;
      this.articleTab = 'edit';
      this.articleForm = {
        id: '',
        categoryName: name,
        title: `${name} 分类文章`,
        content: ''
      };
      try {
        const row = await CategoryArticleApi.getByCategoryName(name);
        if (row) {
          this.articleForm = {
            id: row.id,
            categoryName: row.categoryName,
            title: row.title,
            content: row.content
          };
        }
      } catch (error) {
        ElMessage.error('加载分类文章失败');
      }
    },
    async saveCategoryArticle() {
      if (!this.articleForm.categoryName || !this.articleForm.title) {
        ElMessage.warning('请填写文章标题');
        return;
      }
      this.articleSaving = true;
      try {
        const row = await CategoryArticleApi.saveByCategoryName(this.articleForm.categoryName, {
          articleId: this.articleForm.id || undefined,
          title: this.articleForm.title,
          content: this.articleForm.content,
        });
        this.articleForm.id = row.id;
        ElMessage.success('分类文章保存成功');
        this.articleDialogVisible = false;
      } catch (error) {
        ElMessage.error('分类文章保存失败');
      } finally {
        this.articleSaving = false;
      }
    },
    // 获取所有分类
    fetchCategories() {
      this.loading = true;
      CategoryApi.getCategories(true) // 获取包含排序信息的分类
        .then(res => {
          // 处理返回的数据（可能是字符串数组或对象数组）
          let categoryList = [];
          if (Array.isArray(res) && res.length > 0) {
            if (typeof res[0] === 'string') {
              // 旧格式：字符串数组
              categoryList = res.map((name, index) => ({
                name: name,
                order: index + 1,
                editing: false,
                editName: name
              }));
            } else {
              // 新格式：对象数组
              categoryList = res.map(item => ({
                name: item.name,
                order: item.order || 9999,
                editing: false,
                editName: item.name
              }));
            }
          }
          
          // 按 order 排序
          categoryList.sort((a, b) => a.order - b.order);
          
          this.categories = categoryList;
          this.originalCategories = JSON.parse(JSON.stringify(categoryList)); // 深拷贝保存原始顺序
          this.orderChanged = false;
          
          // 初始化拖拽排序
          this.$nextTick(() => {
            this.initSortable();
          });
        })
        .catch(error => {
          console.error('获取分类失败:', error);
          ElMessage.error('获取分类失败，请重试');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // 初始化拖拽排序
    async initSortable() {
      // 如果 Sortable 还未加载，先加载它
      if (!Sortable) {
        await loadSortable();
      }
      
      if (!Sortable) {
        console.warn('sortablejs 未安装，无法使用拖拽排序功能');
        return;
      }
      
      const tbody = document.querySelector('.el-table__body-wrapper tbody');
      if (!tbody || this.sortableInstance) {
        return;
      }
      
      this.sortableInstance = Sortable.create(tbody, {
        handle: '.drag-handle',
        animation: 150,
        onEnd: (evt) => {
          const { oldIndex, newIndex } = evt;
          if (oldIndex !== newIndex && oldIndex !== undefined && newIndex !== undefined) {
            // 更新 categories 数组顺序
            const movedItem = this.categories.splice(oldIndex, 1)[0];
            this.categories.splice(newIndex, 0, movedItem);
            // 更新 order 值
            this.categories.forEach((item, index) => {
              item.order = index + 1;
            });
            this.orderChanged = true;
          }
        }
      });
    },
    
    // 保存分类排序
    async saveCategoryOrder() {
      try {
        this.savingOrder = true;
        const categoryOrders = this.categories.map((item, index) => ({
          name: item.name,
          order: index + 1
        }));
        
        await CategoryApi.updateCategoryOrder(categoryOrders);
        this.originalCategories = JSON.parse(JSON.stringify(this.categories));
        this.orderChanged = false;
        ElMessage.success('排序保存成功');
      } catch (error) {
        console.error('保存排序失败:', error);
        ElMessage.error('保存排序失败，请重试');
      } finally {
        this.savingOrder = false;
      }
    },
    
    // 重置分类排序
    resetCategoryOrder() {
      this.categories = JSON.parse(JSON.stringify(this.originalCategories));
      this.orderChanged = false;
      ElMessage.info('已重置为原始顺序');
    },
    
    // 添加分类
    addCategory() {
      if (!this.newCategoryName.trim()) {
        ElMessage.warning('请输入分类名称');
        return;
      }
      
      CategoryApi.addCategory(this.newCategoryName.trim())
        .then(() => {
          this.fetchCategories(); // 重新获取分类列表
          this.newCategoryName = ''; // 清空输入框
          ElMessage.success('分类添加成功');
        })
        .catch(error => {
          console.error('添加分类失败:', error);
          ElMessage.error('添加分类失败，请重试');
        });
    },
    
    // 编辑分类（对话框方式，保留兼容）
    editCategory(category) {
      const categoryName = typeof category === 'string' ? category : category.name;
      this.editingCategory = categoryName;
      this.editedCategoryName = categoryName;
      this.editDialogVisible = true;
    },
    
    // 保存编辑（对话框方式）
    saveEdit() {
      if (!this.editedCategoryName.trim()) {
        ElMessage.warning('请输入分类名称');
        return;
      }
      
      CategoryApi.updateCategory(this.editingCategory, this.editedCategoryName.trim())
        .then(() => {
          this.fetchCategories(); // 重新获取分类列表
          this.editDialogVisible = false; // 关闭对话框
          ElMessage.success('分类编辑成功');
        })
        .catch(error => {
          console.error('编辑分类失败:', error);
          ElMessage.error('编辑分类失败，请重试');
        });
    },
    
    // 开始行内编辑
    startEditInline(row) {
      row.editing = true;
      row.editName = row.name;
    },
    
    // 保存行内编辑
    saveEditInline(row) {
      if (!row.editName || !row.editName.trim()) {
        ElMessage.warning('分类名称不能为空');
        row.editName = row.name;
        row.editing = false;
        return;
      }
      
      const newName = row.editName.trim();
      if (newName === row.name) {
        // 名称没有变化，直接取消编辑
        row.editing = false;
        return;
      }
      
      CategoryApi.updateCategory(row.name, newName)
        .then(() => {
          row.name = newName;
          row.editing = false;
          ElMessage.success('分类编辑成功');
        })
        .catch(error => {
          console.error('编辑分类失败:', error);
          ElMessage.error('编辑分类失败，请重试');
          row.editName = row.name;
          row.editing = false;
        });
    },
    
    // 取消行内编辑
    cancelEditInline(row) {
      row.editing = false;
      row.editName = row.name;
    },
    
    // 删除分类
    deleteCategory(category) {
      const categoryName = typeof category === 'string' ? category : category.name;
      ElMessageBox.confirm(`确定要删除分类 "${categoryName}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        CategoryApi.deleteCategory(categoryName)
          .then(() => {
            this.fetchCategories(); // 重新获取分类列表
            ElMessage.success('分类删除成功');
          })
          .catch(error => {
            console.error('删除分类失败:', error);
            ElMessage.error('删除分类失败，请重试');
          });
      }).catch(() => {
        // 用户取消删除
      });
    },
    
    // 批量上传相关方法
    openBulkDialog() {
      this.bulkDialogVisible = true;
      this.bulkJsonText = '';
    },
    resetBulkDialog() {
      this.bulkDialogVisible = false;
      this.bulkJsonText = '';
    },
    handleFileChange(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result;
          this.bulkJsonText = text;
        } catch (error) {
          ElMessage.error('读取文件失败');
        }
      };
      reader.readAsText(file.raw);
    },
    async submitBulkUpload() {
      if (!this.bulkJsonText || this.bulkJsonText.trim() === '') {
        ElMessage.warning('请输入或上传 JSON 数据');
        return;
      }
      
      try {
        const jsonData = JSON.parse(this.bulkJsonText);
        
        // 验证数据格式
        if (!Array.isArray(jsonData) && (!jsonData || typeof jsonData !== 'object')) {
          ElMessage.error('JSON 格式错误：必须是数组格式或对象格式（包含 list 或 stats 字段）');
          return;
        }
        
        if (Array.isArray(jsonData) && jsonData.length === 0) {
          ElMessage.warning('数据为空');
          return;
        }
        
        if (!Array.isArray(jsonData) && !jsonData.list && !jsonData.stats) {
          ElMessage.error('JSON 格式错误：对象格式必须包含 list 或 stats 字段');
          return;
        }
        
        this.bulkLoading = true;
        const result = await CategoryApi.bulkUploadCategories(jsonData);
        this.bulkResult = result;
        this.bulkDialogVisible = false;
        this.bulkResultDialogVisible = true;
        this.activeResultTab = result.failedCount > 0 ? 'failed' : 'success';
        
        if (result.successCount > 0) {
          ElMessage.success(`成功上传 ${result.successCount} 个分类`);
        }
        if (result.failedCount > 0) {
          ElMessage.warning(`${result.failedCount} 个分类上传失败，请查看详情`);
        }
      } catch (error) {
        ElMessage.error(`JSON 解析失败：${error instanceof Error ? error.message : '未知错误'}`);
      } finally {
        this.bulkLoading = false;
      }
    }
  },
  mounted() {
    this.fetchCategories();
  },
  beforeUnmount() {
    // 销毁拖拽实例
    if (this.sortableInstance) {
      this.sortableInstance.destroy();
      this.sortableInstance = null;
    }
  }
};
</script>

<style scoped>
.category-container {
  padding: 20px;
}

.page-title {
  margin-bottom: 20px;
  font-size: 24px;
  color: #333;
}

.add-category-section {
  margin-bottom: 20px;
}

.category-table-section {
  margin-top: 20px;
}

.drag-handle {
  cursor: move;
  user-select: none;
}

.drag-handle:hover {
  color: #409eff;
}

.markdown-preview {
  min-height: 320px;
  max-height: 60vh;
  overflow: auto;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px;
  background-color: #fff;
  line-height: 1.7;
}
</style>